/*
 * Chat messages written but not yet sent (no signal in the field, a deploy's
 * 426 window, the server busy). Each is kept on this device under its
 * `client_key` -- the id the server keeps a message by, so sending one again
 * after a lost answer never makes a second (routes/chat.py) -- until the
 * server has it. In IndexedDB, falling back to memory where there is none
 * (a private window, tests). Every call is wrapped: a blocked store means
 * "nothing kept", never an error in a click handler.
 *
 *   entry: { client_key, channel_id, body, attachments?, created_at, state: 'pending' | 'failed', error? }
 *
 * A photograph's or voice note's file is kept in the entry itself (a Blob,
 * which IndexedDB stores as it is) until the message is sent -- not just
 * until it is uploaded: a server that asks for it again (`upload_again`)
 * gets it again (sendEntry; utils/chat/attachments.js).
 */
import { needsUpload, uploadAttachment, wireAttachment } from './attachments';

const DB_NAME = 'pitriot-chat';
const STORE = 'outbox';

export function memoryBackend() {
  const store = new Map();
  return {
    async getAll() { return [...store.values()]; },
    async put(entry) { store.set(entry.client_key, entry); return entry; },
    async remove(key) { store.delete(key); },
  };
}

export function indexedDbBackend(factory = (typeof indexedDB !== 'undefined' ? indexedDB : null)) {
  if (!factory) return memoryBackend();
  const open = () => new Promise((resolve, reject) => {
    const request = factory.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: 'client_key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  const run = (mode, work) => open().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const result = work(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(result && result.result !== undefined ? result.result : undefined);
    tx.onerror = () => reject(tx.error);
  }));
  return {
    getAll: () => run('readonly', (s) => s.getAll()),
    put: (entry) => run('readwrite', (s) => s.put(entry)).then(() => entry),
    remove: (key) => run('readwrite', (s) => s.delete(key)),
  };
}

export function createChatOutbox(backend = indexedDbBackend()) {
  const safely = async (work, fallback) => {
    try { return await work(); } catch { return fallback; }
  };
  return {
    add: (entry) => safely(() => backend.put({ state: 'pending', ...entry }), entry),
    update: (entry) => safely(() => backend.put(entry), entry),
    remove: (key) => safely(() => backend.remove(key)),
    /** One conversation's unsent messages, oldest first. */
    list: (channelId) => safely(async () => (await backend.getAll())
      .filter((e) => !channelId || e.channel_id === channelId)
      .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))), []),
  };
}

/** A new client_key: a UUID the server accepts. */
export function newClientKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const hex = () => Math.floor(Math.random() * 16).toString(16);
  const part = (n) => Array.from({ length: n }, hex).join('');
  return `${part(8)}-${part(4)}-4${part(3)}-${'89ab'[Math.floor(Math.random() * 4)]}${part(3)}-${part(12)}`;
}

/**
 * Send one entry: its files up first -- each marked `uploaded` and the entry
 * saved after each, so a retry puts up only what is still missing -- then
 * the message. A refusal that names an upload as gone, withdrawn or not the
 * size declared (`upload_again`) puts that file up again and sends once
 * more, at most `tries` times. Resolves the server's message (undefined when
 * the answer had none); rejects as the last request failed, after saving
 * what was uploaded.
 *
 *   client   the API client (utils/api.js)
 *   save     (entry) => Promise, keeps the entry's progress (the outbox's update)
 */
export async function sendEntry(entry, { client, save = async (e) => e, upload = uploadAttachment, tries = 2 }) {
  let current = entry;
  const swap = (id, change) => {
    current = { ...current, attachments: current.attachments.map((a) => (a.id === id ? change(a) : a)) };
  };
  for (let attempt = 0; ; attempt += 1) {
    for (const item of current.attachments || []) {
      if (!needsUpload(item)) continue;
      const uploaded = await upload(client, current.channel_id, item);
      swap(item.id, (a) => ({ ...a, uploaded }));
      await save(current);
    }
    const attachments = current.attachments || [];
    try {
      const r = await client.post(`/chat/channels/${current.channel_id}/messages`, {
        client_key: current.client_key,
        ...(current.body ? { body: current.body } : {}),
        ...(attachments.length ? { attachments: attachments.map(wireAttachment) } : {}),
      });
      return r?.data?.message;
    } catch (error) {
      const data = error?.response?.data;
      if (!data?.upload_again || attempt >= tries) throw error;
      const named = data.attachment_id;
      attachments.filter((a) => a.uploaded && (!named || a.uploaded.attachment_id === named))
        .forEach((a) => swap(a.id, (was) => ({ ...was, uploaded: null })));
      await save(current);
    }
  }
}

/** Whether a failed send is worth trying again later (no answer, the server
 *  busy or mid-deploy), rather than refused for good (a 4xx about the message). */
export function retryable(error) {
  const status = error?.response?.status;
  return !status || status >= 500 || status === 429 || status === 426 || status === 408;
}
